import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { extractApiError } from '../../utils/extractApiError';
import { PIN_ERROR_SOURCE } from '../../utils/popupFailure';
import { dispatch, store } from '../store';
import { updateError } from './user';

// ─── Pinned leads ─────────────────────────────────────────────────────────────
// A practice-wide list of leads that staff have parked part-way through
// handling ("waiting on tech support", "call back after 3pm"…). One active pin
// per lead per practice; anyone in the practice can unpin, only the pinner can
// edit the note (the API returns 403 otherwise).
//
// The whole practice list is loaded (`scope=all`) and "My pins" is derived on
// the client by comparing `pinned_by_detail.id` with the current PracticeUser
// id — see selectMyLeadPins. The backend caps a practice at 50 pins, so one
// page of 50 always holds the full list.
//
// Not persisted. Wiped on practice switch by the root reducer's default reset
// (this slice is NOT in PRACTICE_AGNOSTIC_SLICES) and on logout via
// resetLeadPins(). Some practice-change paths skip the switch reset, so the
// slice records which practice its list belongs to (`practiceId`) and readers
// (useLeadPins) ignore — and refetch — a list for any other practice.

export const LEAD_PINS_PAGE_LIMIT = 50;
export const LEAD_PIN_REASON_MAX = 500;

// Tag on every toast these thunks raise (defined in utils/popupFailure so the
// classifier can share it without importing this slice). PopupFailureProvider
// ignores it, so a pin's own toast never replaces the popup failure being pinned.
export { PIN_ERROR_SOURCE };

// Every pin request: callApi must not dispatch its own unmarked 500/503 toast
// ("Something went wrong! (/…/lead-pins/)") — that would count as a new popup
// failure. The thunks' marked toasts already tell the user.
const PIN_CALL_OPTIONS = { suppressServerErrorDispatch: true };

const leadPinsUrl = (practiceId) => `/api/v1/practices/${practiceId}/lead-pins/`;

const initialState = {
    pins: [],
    loading: false,
    loaded: false,
    practiceId: null,
};

const slice = createSlice({
    name: 'leadPins',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setPins(state, action) {
            state.pins = action.payload.pins;
            state.practiceId = action.payload.practiceId;
            state.loading = false;
            state.loaded = true;
        },
        addPin(state, action) {
            state.pins = [action.payload, ...state.pins.filter((p) => p.id !== action.payload.id && p.lead !== action.payload.lead)];
        },
        replacePin(state, action) {
            state.pins = state.pins.map((p) => (p.id === action.payload.id ? action.payload : p));
        },
        removePin(state, action) {
            state.pins = state.pins.filter((p) => p.id !== action.payload);
        },
        reset() {
            return initialState;
        },
    },
});

export default slice.reducer;

export const { reset, setPins, addPin, replacePin, removePin } = slice.actions;

// ─── Selectors / helpers ──────────────────────────────────────────────────────

export const findPinForLead = (pins, leadId) =>
    (pins || []).find((p) => String(p.lead) === String(leadId)) || null;

export const isOwnPin = (pin, practiceUserId) =>
    Boolean(pin && practiceUserId) && String(pin?.pinned_by_detail?.id) === String(practiceUserId);

export const selectMyLeadPins = (pins, practiceUserId) => (pins || []).filter((p) => isOwnPin(p, practiceUserId));

// The 400 for "duplicate / cap / bad lead" arrives either as `detail`,
// `non_field_errors` or a field list (`lead: [...]`). For a field list show the
// message itself rather than extractApiError's "lead: …" debug prefix.
const pinErrorMessage = (response, fallback) => {
    const data = response?.data;
    if (data && typeof data === 'object') {
        const fieldMsg = ['lead', 'reason'].map((k) => data[k]).find((v) => Array.isArray(v) && v.length);
        if (fieldMsg && !data.detail && !data.non_field_errors) return String(fieldMsg[0]);
    }
    if (response?.status === 403 && !data?.detail) return 'Only the person who pinned this lead can edit its note.';
    return extractApiError(response, fallback);
};

// ─── Stale-response guards ────────────────────────────────────────────────────
// Every request captures `requestGeneration` when it starts. resetLeadPins()
// (logout / identity change) bumps it, so a response that was in flight for the
// previous session is dropped instead of repopulating the cleared slice — same
// pattern as invalidatePermissionsRequests() / resetScopedAdmin().
let requestGeneration = 0;

export function invalidateLeadPinsRequests() {
    requestGeneration += 1;
}

// Logout / identity change: drop in-flight responses, then clear the slice.
export function resetLeadPins() {
    return () => {
        invalidateLeadPinsRequests();
        dispatch(slice.actions.reset());
    };
}

const getState = () => store?.getState?.() || {};

// A null current practice (logged out, or not yet selected) is never a match:
// a response landing then belongs to nobody on screen and is dropped.
const isCurrentPractice = (practiceId) => {
    const current = getState().practiceTreatmentList?.practiceId;
    return current != null && practiceId != null && String(current) === String(practiceId);
};

// May a response for `practiceId`, started at generation `gen`, touch the slice?
const isFresh = (practiceId, gen) => gen === requestGeneration && isCurrentPractice(practiceId);

// Local list updates after a mutation only apply when the slice currently holds
// THAT practice's pins — never splice practice A's pin into practice B's list.
const sliceHoldsPractice = (practiceId) => {
    const held = getState().leadPins?.practiceId;
    return held != null && String(held) === String(practiceId);
};
const canApplyMutation = (practiceId, gen) => isFresh(practiceId, gen) && sliceHoldsPractice(practiceId);

// ─── Thunks ───────────────────────────────────────────────────────────────────

export function getLeadPins(practiceId) {
    return async () => {
        if (!practiceId) return;
        const gen = requestGeneration;
        dispatch(slice.actions.setLoading(true));
        try {
            const response = await callApi(`${leadPinsUrl(practiceId)}?scope=all&limit=${LEAD_PINS_PAGE_LIMIT}&offset=0`, 'GET', null, false, PIN_CALL_OPTIONS);
            // A response from before a logout / for a practice the user has since
            // switched away from must not repopulate the slice.
            if (!isFresh(practiceId, gen)) return;
            if (response?.status === 200) {
                const { data } = response;
                let pins = [];
                if (Array.isArray(data?.results)) pins = data.results;
                else if (Array.isArray(data)) pins = data;
                dispatch(slice.actions.setPins({ pins, practiceId }));
            } else {
                // Silent: the pin icon is a background surface (timeouts come
                // back as status 0; 429/5xx land here too). Mark loaded so
                // callers don't refetch in a loop, but only keep the existing
                // list when it already belongs to THIS practice — never relabel
                // another practice's pins as this one's.
                const keep = sliceHoldsPractice(practiceId) ? getState().leadPins?.pins || [] : [];
                dispatch(slice.actions.setPins({ pins: keep, practiceId }));
            }
        } catch (err) {
            if (isFresh(practiceId, gen)) dispatch(slice.actions.setLoading(false));
        }
    };
}

// `error` ({ message, context }) is optional: when given, the failure the user
// hit is recorded on the new pin in the same request. The key is omitted
// entirely otherwise.
export function pinLead(practiceId, leadId, reason, onSuccess, error) {
    return async () => {
        const gen = requestGeneration;
        try {
            const body = { lead: leadId, reason: reason || '' };
            if (error) body.error = error;
            const response = await callApi(leadPinsUrl(practiceId), 'POST', body, false, PIN_CALL_OPTIONS);
            if (response?.status === 201 || response?.status === 200) {
                if (canApplyMutation(practiceId, gen)) dispatch(slice.actions.addPin(response.data));
                dispatch(updateError({ source: PIN_ERROR_SOURCE, success: true, message: 'Lead pinned' }));
                if (onSuccess) onSuccess(response.data);
                return true;
            }
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: pinErrorMessage(response, 'Could not pin this lead') }));
            // A duplicate means someone else pinned it meanwhile — resync.
            if (response?.status === 400 && isFresh(practiceId, gen)) dispatch(getLeadPins(practiceId));
        } catch (err) {
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: err?.message || 'Could not pin this lead' }));
        }
        return false;
    };
}

// Record another failure on an existing pin. The API answers 201 with the full
// pin row, which replaces the one in the slice.
export function attachPinError(practiceId, pinId, error) {
    return async () => {
        const gen = requestGeneration;
        try {
            const response = await callApi(`${leadPinsUrl(practiceId)}${pinId}/errors/`, 'POST', error, false, PIN_CALL_OPTIONS);
            if (response?.status === 201) {
                if (canApplyMutation(practiceId, gen)) dispatch(slice.actions.replacePin(response.data));
                dispatch(updateError({ source: PIN_ERROR_SOURCE, success: true, message: 'Error added to the pin' }));
                return true;
            }
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: pinErrorMessage(response, 'Could not add the error to the pin') }));
        } catch (err) {
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: err?.message || 'Could not add the error to the pin' }));
        }
        return false;
    };
}

export function updateLeadPin(practiceId, pinId, reason, onSuccess) {
    return async () => {
        const gen = requestGeneration;
        try {
            const response = await callApi(`${leadPinsUrl(practiceId)}${pinId}/`, 'PATCH', { reason: reason || '' }, false, PIN_CALL_OPTIONS);
            if (response?.status === 200) {
                if (canApplyMutation(practiceId, gen)) dispatch(slice.actions.replacePin(response.data));
                dispatch(updateError({ source: PIN_ERROR_SOURCE, success: true, message: 'Pin note updated' }));
                if (onSuccess) onSuccess(response.data);
                return true;
            }
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: pinErrorMessage(response, 'Could not update the pin note') }));
        } catch (err) {
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: err?.message || 'Could not update the pin note' }));
        }
        return false;
    };
}

export function unpinLead(practiceId, pinId, onSuccess) {
    return async () => {
        const gen = requestGeneration;
        try {
            const response = await callApi(`${leadPinsUrl(practiceId)}${pinId}/`, 'DELETE', null, false, PIN_CALL_OPTIONS);
            // 404: already unpinned by someone else — the outcome the user wanted.
            if (response?.status === 204 || response?.status === 200 || response?.status === 404) {
                if (canApplyMutation(practiceId, gen)) dispatch(slice.actions.removePin(pinId));
                dispatch(updateError({ source: PIN_ERROR_SOURCE, success: true, message: 'Lead unpinned' }));
                if (onSuccess) onSuccess();
                return true;
            }
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: pinErrorMessage(response, 'Could not unpin this lead') }));
        } catch (err) {
            dispatch(updateError({ source: PIN_ERROR_SOURCE, success: false, message: err?.message || 'Could not unpin this lead' }));
        }
        return false;
    };
}

// "you" for your own pins, otherwise the pinner's first name (falls back to
// last name, then "a colleague" when the user record has no name).
export const pinnedByName = (pin, practiceUserId) => {
    if (isOwnPin(pin, practiceUserId)) return 'you';
    const by = pin?.pinned_by_detail || {};
    return (by.first_name || by.last_name || 'a colleague').trim();
};
